import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  PlayCircle as PlayIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Videocam as VideoIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import api from '../utils/api';

export default function LineVideoManager({
  passageId,
  passageText,
  passageType,
  lineVideos = [],
  onVideosChange,
  disabled = false
}) {
  const [uploadingLine, setUploadingLine] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);
  const currentLineIndexRef = useRef(null);

  // Parse text into lines/paragraphs
  const segments = passageType === 'story'
    ? passageText.split('\n\n').filter(p => p.trim())
    : passageText.split('\n').filter(line => line.trim());

  // Get video for a specific line
  const getVideoForLine = useCallback((lineIndex) => {
    return lineVideos.find(v => v.lineIndex === lineIndex);
  }, [lineVideos]);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const lineIndex = currentLineIndexRef.current;
    if (lineIndex === null) return;

    uploadVideo(file, lineIndex);
    event.target.value = '';
  };

  // Upload video to server
  const uploadVideo = async (file, lineIndex) => {
    if (!passageId) {
      setSnackbar({
        open: true,
        message: 'Please save the passage first before adding videos',
        severity: 'warning'
      });
      return;
    }

    setUploadingLine(lineIndex);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('video', file);
    formData.append('lineIndex', lineIndex.toString());

    try {
      const response = await api.post(`/api/passages/${passageId}/line-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      // Update the videos list
      if (onVideosChange) {
        const newVideos = [...lineVideos.filter(v => v.lineIndex !== lineIndex), response.data.video];
        newVideos.sort((a, b) => a.lineIndex - b.lineIndex);
        onVideosChange(newVideos);
      }

      setSnackbar({ open: true, message: 'Video uploaded successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error uploading video:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to upload video',
        severity: 'error'
      });
    } finally {
      setUploadingLine(null);
      setUploadProgress(0);
    }
  };

  // Delete video
  const handleDeleteVideo = async () => {
    if (deleteConfirm === null) return;

    try {
      await api.delete(`/api/passages/${passageId}/line-video/${deleteConfirm}`);

      // Update the videos list
      if (onVideosChange) {
        const newVideos = lineVideos.filter(v => v.lineIndex !== deleteConfirm);
        onVideosChange(newVideos);
      }

      setSnackbar({ open: true, message: 'Video deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('Error deleting video:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete video',
        severity: 'error'
      });
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Trigger file input for a specific line
  const handleAddVideo = (lineIndex) => {
    currentLineIndexRef.current = lineIndex;
    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        pb: 1,
        borderBottom: '1px solid rgba(0,0,0,0.1)'
      }}>
        <VideoIcon sx={{ color: '#9c27b0' }} />
        <Typography variant="subtitle1" fontWeight={600} color="#9c27b0">
          Video Instructions
        </Typography>
        <Chip
          label={`${lineVideos.length} video${lineVideos.length !== 1 ? 's' : ''}`}
          size="small"
          color={lineVideos.length > 0 ? 'secondary' : 'default'}
          sx={{ fontWeight: 600 }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add video instructions that will play before each {passageType === 'story' ? 'paragraph' : 'line'}.
        Click the + button to add a video.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {segments.map((segment, index) => {
          const video = getVideoForLine(index);
          const isUploading = uploadingLine === index;

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}
            >
              {/* Video indicator/button row */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minHeight: 36
              }}>
                {video ? (
                  // Video exists - show preview button and delete
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Play video preview">
                      <IconButton
                        size="small"
                        onClick={() => setPreviewVideo(video)}
                        sx={{
                          bgcolor: '#9c27b0',
                          color: 'white',
                          '&:hover': { bgcolor: '#7b1fa2', transform: 'scale(1.1)' },
                          transition: 'all 0.2s'
                        }}
                      >
                        <PlayIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Chip
                      icon={<CheckIcon sx={{ fontSize: 14 }} />}
                      label={video.fileName?.slice(0, 20) || 'Video'}
                      size="small"
                      color="secondary"
                      sx={{
                        maxWidth: 150,
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }
                      }}
                    />
                    <Tooltip title="Remove video">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteConfirm(index)}
                        sx={{
                          color: '#f44336',
                          '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' }
                        }}
                        disabled={disabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : isUploading ? (
                  // Uploading state
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
                    <CircularProgress size={20} color="secondary" />
                    <Box sx={{ flex: 1, minWidth: 100 }}>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        color="secondary"
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {uploadProgress}%
                    </Typography>
                  </Box>
                ) : (
                  // No video - show add button
                  <Tooltip title={passageId ? `Add video before ${passageType === 'story' ? 'paragraph' : 'line'} ${index + 1}` : 'Save passage first to add videos'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleAddVideo(index)}
                        disabled={disabled || !passageId}
                        sx={{
                          border: '2px dashed #9c27b0',
                          color: '#9c27b0',
                          '&:hover': {
                            bgcolor: 'rgba(156, 39, 176, 0.1)',
                            transform: 'scale(1.1)'
                          },
                          '&:disabled': {
                            border: '2px dashed #ccc',
                            color: '#ccc'
                          },
                          transition: 'all 0.2s'
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>

              {/* Line content preview */}
              <Box sx={{
                p: 1.5,
                background: '#f8f9fa',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                borderLeft: video
                  ? '4px solid #9c27b0'
                  : passageType === 'story'
                    ? '4px solid #e0e0e0'
                    : '4px solid #e0e0e0',
                ml: 1
              }}>
                <Typography variant="caption" color={video ? '#9c27b0' : passageType === 'story' ? '#9c27b0' : '#2196f3'} fontWeight={600}>
                  {passageType === 'story' ? `Paragraph ${index + 1}` : `Line ${index + 1}`}
                  {video && ' (has video)'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.4, color: '#333' }}>
                  {segment.trim().slice(0, 100)}{segment.trim().length > 100 ? '...' : ''}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
        style={{ display: 'none' }}
      />

      {/* Video Preview Dialog */}
      <Dialog
        open={!!previewVideo}
        onClose={() => setPreviewVideo(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#9c27b0',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VideoIcon />
            <span>Video Preview - {passageType === 'story' ? 'Paragraph' : 'Line'} {previewVideo?.lineIndex !== undefined ? previewVideo.lineIndex + 1 : ''}</span>
          </Box>
          <IconButton onClick={() => setPreviewVideo(null)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
          {previewVideo && (
            <video
              src={previewVideo.videoUrl}
              controls
              autoPlay
              style={{
                width: '100%',
                maxHeight: '70vh',
                display: 'block'
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#f5f5f5' }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
            {previewVideo?.fileName}
          </Typography>
          <Button onClick={() => setPreviewVideo(null)} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete Video?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the video for {passageType === 'story' ? 'paragraph' : 'line'} {deleteConfirm !== null ? deleteConfirm + 1 : ''}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDeleteVideo} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
